import type { CmlContent } from 'bundles/cml';
import NaptimeResource from 'bundles/naptimejs/resources/NaptimeResource';
import { requireFields } from 'bundles/naptimejs/util/requireFieldsDecorator';

import _t from 'i18n!nls/naptimejs';

/**
 * The key and value for this enum was chosen to be consistent with the [[CourseType]] BE model.
 * For more details see https://coursera.atlassian.net/wiki/spaces/B2C/pages/2466578543/Course+Types
 */
export enum CourseType {
  STANDARD_COURSE = 'standardCourse',
  RHYME_PROJECT = 'rhymeProject',
  PROJECT = 'project',
  VERIFIED_SKILLS_COURSE = 'verifiedSkillsCourse',
}

export type StandardCourseMetadata = {
  typeNameIndex: typeof CourseType.STANDARD_COURSE;
};

export type ProjectMetadata = {
  typeNameIndex: typeof CourseType.PROJECT;
};

export type RhymeProjectMetadata = {
  typeNameIndex: typeof CourseType.RHYME_PROJECT;
  tasks?: Array<CmlContent>;
  screenshotUrls?: Array<string>;
};

export type VerifiedSkillsCourseMetadata = {
  typeNameIndex: typeof CourseType.VERIFIED_SKILLS_COURSE;
};

export type CourseTypeMetadata = {
  typeName: CourseType;
  definition: StandardCourseMetadata | RhymeProjectMetadata | ProjectMetadata | VerifiedSkillsCourseMetadata | {};
};

export type CourseTypeMetadataResponse = {
  courseTypeMetadata: CourseTypeMetadata;
  version: number;
  id: string;
};

export type CourseTypeMetadataUpdate = {
  courseTypeMetadata: {
    typeName: CourseType;
    definition: StandardCourseMetadata | RhymeProjectMetadata | ProjectMetadata | VerifiedSkillsCourseMetadata;
  };
  nextVersion: number;
};

export class CourseTypeMetadataV1 extends NaptimeResource {
  static RESOURCE_NAME = 'courseTypeMetadata.v1';

  declare courseTypeMetadata: CourseTypeMetadata;

  declare version: number;

  declare id: string;

  static courseTypeMetadatas(courseIds: Array<string>, required = false) {
    return CourseTypeMetadataV1.multiGet(courseIds, {
      fields: ['courseTypeMetadata'],
      required,
    });
  }

  @requireFields('courseTypeMetadata')
  get isCourse(): boolean {
    return this.courseTypeMetadata.typeName === CourseType.STANDARD_COURSE;
  }

  @requireFields('courseTypeMetadata')
  get isGuidedProject(): boolean {
    return this.courseTypeMetadata.typeName === CourseType.RHYME_PROJECT;
  }

  @requireFields('courseTypeMetadata')
  get isProject(): boolean {
    return this.courseTypeMetadata.typeName === CourseType.PROJECT;
  }

  @requireFields('courseTypeMetadata')
  get isVerifiedSkillsCourse(): boolean {
    return this.courseTypeMetadata.typeName === CourseType.VERIFIED_SKILLS_COURSE;
  }

  @requireFields('courseTypeMetadata')
  get courseLabel(): string | undefined {
    switch (this.courseTypeMetadata.typeName) {
      case CourseType.RHYME_PROJECT:
        return _t('Guided Project');
      case CourseType.STANDARD_COURSE:
      case CourseType.VERIFIED_SKILLS_COURSE:
        return _t('course');
      case CourseType.PROJECT:
        return _t('Project');
      default:
        throw new Error(`The typeName ${this.courseTypeMetadata.typeName} is not supported.`);
    }
  }
}

export default CourseTypeMetadataV1;
